import { Link, Radio } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import { Heading, SearchBar } from "../../../shared/ReusableComponents";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { caseRepository } from "../../../services/caseRepository";
import { useDispatch, useSelector } from "react-redux";
import { DateFormatter } from "../../../shared/utils/DateFormatter";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { AlertType, ICONS, UserRoles } from "../../../shared/constants/enums";
import CircularProgressDialog from "../../../shared/ReusableComponents/CircularProgressDialog";
import ConfirmDialog from "../../../shared/ReusableComponents/ConfirmDialog";
import usePost from "../../../services/hooks/usePost";
import { hideToast, toast } from "../../../store/toastSlice";
import { apiEndpoints } from "../../../shared/constants/api-constant";

export const TemplateLibrary = ({ userRole }: { userRole: any }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [templateData, setTemplateData] = useState<any[]>([]);
  const [filteredTemplateData, setFilteredTemplateData] =
    useState<any[]>(templateData);
  const [searchText, setSearchText] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [assignErrorMessage, setAssignErrorMessage] = useState<string>("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [circularDialogOpen, setCircularDialogOpen] = useState(false);
  const userInfo = useSelector((state: any) => state.login?.userInfo);
  const [cloneErrorMessage, setCloneErrorMessage] = useState<string>("");
  const { data: postResponse, postService } = usePost();
  const convertedTemplateId = CommonFunctions.ConvertIdToDisplayId(
    selectedGroup,
    "template"
  );

  const renderTemplate = (params: any) => {
    if (CommonFunctions.IsCurrentSuperUser()) {
      return (
        <Link
          onClick={() => navigate(`/admin/template/${params.row.id}`)}
          underline="always"
          sx={{ cursor: "pointer" }}
        >
          {params.value}
        </Link>
      );
    } else if (CommonFunctions.IsCurrentUserSubCoordinator()) {
      return (
        <Link
          onClick={() => navigate(`/coordinator/template/${params.row.id}`)}
          underline="always"
          sx={{ cursor: "pointer" }}
        >
          {params.value}
        </Link>
      );
    }
  };

  const renderRadio = (params: any) => {
    const handleChangeRadio = (id: any) => {
      setSelectedGroup(id);
      setAssignErrorMessage("");
      setCloneErrorMessage("")
      const selectedRow = filteredTemplateData.find(
        (row: any) => row.id === id
      );
    };
    return (
      <Radio
        checked={params.id === selectedGroup}
        onChange={() => handleChangeRadio(params.id)}
        value={params.id}
        name="radio-buttons"
        inputProps={{ "aria-label": `${params.id}` }}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: "",
      headerName: "",
      renderCell: renderRadio,
      width: 70,
      resizable: false,
    },
    {
      field: "templateId",
      headerName: "Template ID",
      renderCell: renderTemplate,
    },
    {
      field: "templateDescription",
      headerName: "Template Description ",
    },
    {
      field: "mrn",
      headerName: "MRN",
    },
    {
      field: "patientName",
      headerName: "Patient Name",
      valueGetter: (value, row) => {
        return `${row.patientFirstName} ${
          row.patientMiddleName ? row.patientMiddleName + " " : ""
        }${row.patientLastName}`.trim();
      },
    },
    {
      field: "dob",
      headerName: "Age",
      valueGetter: (value, row) => DateFormatter.calculateAge(row.dob),
    },
    {
      field: "isATSI",
      headerName: "Aboriginal and Torres Strait Islander",
      valueGetter: (value, row) => (row.isATSI === true ? "Yes" : "No"),
    },
    {
      field: "wardName",
      headerName: "Ward Name",
    },
    {
      field: "diagnosis",
      headerName: "Admission Diagnosis",
      valueGetter: (value, row) =>
        CommonFunctions.getDisplayValue(row.diagnosis),
    },
  ];

  const handleAssignTemplate = () => {
    if (!selectedGroup) {
      setAssignErrorMessage("Please select template");
      return;
    }
    if (userRole === UserRoles.SUPER_USER) {
      navigate(`/admin/templatelibrary/assignStudent/${selectedGroup}`);
    }
    if (userRole === UserRoles.SUBJECT_COORDINATOR) {
      navigate(`/coordinator/templatelibrary/assignStudent/${selectedGroup}`);
    }
    setAssignErrorMessage("");
  };

  const handleSearch = (searchText: string) => {
    setSearchText(searchText);
    if (!searchText) {
      setFilteredTemplateData(templateData);
    } else {
      const filteredRows = templateData?.filter(
        (row: any) =>
          row.templateId &&
          row.templateId
            .toString()
            .toLowerCase()
            .includes(searchText.toLowerCase())
      );

      if (filteredRows.length === 0) {
        setFilteredTemplateData([]);
      } else {
        setFilteredTemplateData(filteredRows);
      }
    }
  };

  React.useEffect(() => {
    if (!userInfo) return;
    dispatch(showLoader());
    caseRepository.getMasterTemplateList().then((data) => {
      if (data && Array.isArray(data)) {
        const updatedData = data.map((x) => {
          return {
            ...x,
            templateId: CommonFunctions.ConvertIdToDisplayId(x.id, "template"),
          };
        });
        setTemplateData(updatedData);
        setFilteredTemplateData(updatedData);
      }
      dispatch(hideLoader());
    });
  }, [dispatch]);

  const handleConfirm = async () => {
    setConfirmationDialogOpen(false);
    setCircularDialogOpen(true);
    try {
      const originalDisplayId = CommonFunctions.ConvertIdToDisplayId(
        selectedGroup,
        "template"
      );

      const payload = {
        templatedId: selectedGroup,
      };

      await postService(
        `${apiEndpoints.CLONE_TEMPLATE}/${selectedGroup}`,
        payload
      );
      dispatch(hideToast());

      dispatch(showLoader());
      const updatedTemplates = await caseRepository.getMasterTemplateList();
      dispatch(hideLoader());

      const existingIds = templateData.map((t) => t.id);

      if (Array.isArray(updatedTemplates)) {
        const newTemplate = updatedTemplates.find(
          (t) => !existingIds.includes(t.id)
        );

        const finalTemplates = updatedTemplates.map((x) => {
          return {
            ...x,
            templateId: CommonFunctions.ConvertIdToDisplayId(x.id, "template"),
          };
        });

        setTemplateData(finalTemplates);
        setFilteredTemplateData(finalTemplates);

        const newDisplayId = newTemplate
          ? CommonFunctions.ConvertIdToDisplayId(newTemplate.id, "template")
          : "";

        dispatch(
          toast({
            message: `Template '${originalDisplayId}' has been successfully cloned as '${newDisplayId}'.`,
            type: AlertType.SUCCESS,
          })
        );
      }
    } catch (err) {
      dispatch(
        toast({
          message:
            "An error occurred while cloning the template. Please try again later.",
          type: AlertType.ERROR,
        })
      );
    } finally {
      setCircularDialogOpen(false);
      setSelectedGroup(null);
    }
  };

  const handleCloneTemplate = () => {
    if (!selectedGroup) {
      setCloneErrorMessage("Please select a template to clone");
      return;
    }
    setCloneErrorMessage("");
    setConfirmationDialogOpen(true);
  };

  return (
    <div style={{ backgroundColor: "#fff", padding: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 20px 0px 20px",
        }}
      >
        <div style={{ fontSize: "20px", display: "flex" }}>
          <span style={{ fontWeight: 700 }}>Template List</span>
          <span className="temp-buttons-align">
            <IconButton
              onClick={handleAssignTemplate}
              className="temp-buttons"
              text="Assign to Students"
              icon={
                <span className="material-symbols-outlined">local_library</span>
              }
            />
            {assignErrorMessage && (
              <Heading
                text={assignErrorMessage}
                color="error.main"
                type="body1"
                style={{ marginLeft: "10px", marginTop: "4px" }}
              />
            )}
          </span>
          {CommonFunctions.IsCurrentSuperUser() ? (
            <span className="temp-buttons-align">
              <IconButton
                variant="contained"
                onClick={handleCloneTemplate}
                className="temp-buttons"
                sx={{
                  marginLeft: "20px !important",
                  backgroundColor: "transparent",
                  color: "var(--color-primary-main) !important",
                  border: "1px solid var(--color-primary-main) !important",
                  width: "max-content",
                }}
                text="Clone Template"
                icon={
                  <span className="material-symbols-outlined">file_copy</span>
                }
              />
              {cloneErrorMessage && (
                <Heading
                  text={cloneErrorMessage}
                  color="error.main"
                  type="body1"
                  style={{ marginTop: "4px", marginLeft: "20px" }}
                />
              )}
            </span>
          ) : (
            ""
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <SearchBar
            value={searchText}
            onSearch={handleSearch}
            placeholder="Search by Template ID"
            className="search-bar-temp-width"
          />
        </div>
      </div>
      <GridTable
        rows={filteredTemplateData}
        columns={columns}
        pageSizeOptions={[5, 10, 15]}
        initialPageSize={10}
      />
      <ConfirmDialog
        dialogOpen={confirmationDialogOpen}
        setDialogOpen={setConfirmationDialogOpen}
        text="Are you sure you want to save this template as a clone?"
        handleConfirm={handleConfirm}
        confirmText="Yes"
        cancelText="No"
        width="sm"
        icon={ICONS.WARNING}
      />
      <CircularProgressDialog
        dialogOpen={circularDialogOpen}
        text={`Cloning of the current template "${convertedTemplateId}" is in progress. Please wait until the process completes.  A confirmation message will appear once cloning is finished.`}
        showButtons={false}
        icon={ICONS.WARNING}
      />
    </div>
  );
};
